import { useEffect, useState } from 'react';
import { Share, X } from 'lucide-react';
import { detectIos, detectStandalone } from '@/lib/platform';

/**
 * "Add to home screen" banner.
 *
 * Nothing does this for us. Chrome fires `beforeinstallprompt`, which we hold
 * and fire on a tap; iOS Safari never fires it, so there the only route is
 * Share -> Add to Home Screen and the banner has to spell that out. Desktop
 * Safari and Firefox have no install path at all, so they get nothing rather
 * than a button that cannot work.
 *
 * An employee who already installed the app months ago must never see this, so
 * standalone display mode wins over everything, and a dismissal is remembered.
 *
 * Deliberately rendered inside the authenticated shell, never on the login
 * screen: interrupting someone mid-login with a pitch is the fastest way to
 * get it dismissed for good.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'hrma.installPrompt.dismissed';

/**
 * Chromium browsers (Chrome, Edge, Opera, Samsung) all have an "Install app"
 * menu entry even when they have not fired the event yet, so the banner can
 * still show a working route. Firefox and desktop Safari have none, and telling
 * those users to install would be a dead end.
 */
function detectChromium(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return !/Firefox|FxiOS/.test(ua) && /Chrome|Chromium|CriOS|Edg\/|OPR\//.test(ua);
}

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false; // private mode / storage blocked: fall through to showing it
  }
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(readDismissed);
  const [standalone, setStandalone] = useState(detectStandalone);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      // Suppress Chrome's own mini-infobar so there is one prompt, not two.
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setStandalone(true);
      setDeferred(null);
    };

    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);

    // main.tsx captures the event at module scope, before this component mounts.
    const early = (window as { __hrmaInstallPrompt?: Event }).__hrmaInstallPrompt;
    if (early) setDeferred(early as BeforeInstallPromptEvent);

    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // Let the first screen settle before asking anything of the employee.
  useEffect(() => {
    if (standalone || dismissed) return;
    const timer = window.setTimeout(() => setVisible(true), 4000);
    return () => window.clearTimeout(timer);
  }, [standalone, dismissed]);

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* storage blocked: dismissing still works for this session */
    }
  };

  const install = async () => {
    if (!deferred) return;
    await deferred.prompt();
    const choice = await deferred.userChoice.catch(() => null);
    if (choice?.outcome === 'accepted') {
      setStandalone(true);
    } else {
      dismiss(); // said no once: do not ask again
    }
    setDeferred(null);
  };

  if (standalone || dismissed || !visible) return null;

  const ios = detectIos();
  const chromium = detectChromium();
  // Nothing to offer outside Chromium and iOS: both have an install path, the
  // rest do not, and a button that cannot work is worse than no banner.
  if (!deferred && !ios && !chromium) return null;

  return (
    <div
      role="dialog"
      aria-label="Install hrma"
      className="fixed inset-x-3 bottom-3 z-50 rounded-xl border border-border bg-background p-3 shadow-lg sm:inset-x-auto sm:right-4 sm:w-96"
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">Install hrma</p>
          {deferred ? (
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              Pasang untuk buka terus dan clock-in lebih pantas, tanpa menaip alamat.
            </p>
          ) : ios ? (
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              Tekan <Share className="mx-0.5 inline h-3.5 w-3.5 align-text-bottom" aria-label="Share" />{' '}
              di bar bawah Safari, kemudian pilih <strong>Add to Home Screen</strong>. Ikon hrma akan
              muncul di skrin utama anda.
            </p>
          ) : (
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              Buka menu pelayar (⋮) dan pilih <strong>Install app</strong>. Ikon hrma akan muncul di
              skrin utama anda.
            </p>
          )}
          {deferred ? (
            <button
              type="button"
              onClick={install}
              className="mt-2 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90"
            >
              Install
            </button>
          ) : null}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Tutup"
          className="shrink-0 rounded-lg p-1 text-muted-foreground hover:bg-muted"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
